import axios from "axios";
import { getAxiosInstance } from "@utils/axios-config/axios_config";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import { handleAuthFailure } from "@utils/auth/authFailure";
import type {
  ApkInspection,
  AudienceTarget,
  AudienceTargets,
  DefaultVersion,
  FamilyUpload,
  FleetSummary,
  HardwareFamily,
  HeadsetGroupBy,
  HeadsetGroupsResult,
  HeadsetProgressFilter,
  HeadsetRelease,
  HeadsetSelection,
  InstallMode,
  ReleaseHeadsetPage,
  ReleaseOffer,
  ReleaseSettings,
  StoredApk,
} from "types/headsetReleaseTypes";
import { HEADSET_RELEASES_PATH } from "./ApiConstants";

const PARALLEL_PART_UPLOADS = 3;
const APK_UPLOAD_COMPLETE_TIMEOUT_MS = 120_000;

function authorized() {
  return { headers: { Authorization: `Bearer ${SecureTokenManager.getAccessToken()}` } };
}

async function request<T>(send: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await send()).data;
  } catch (error) {
    handleAuthFailure(error);
    throw error;
  }
}

const releasePath = (version: string) => `${HEADSET_RELEASES_PATH}/${encodeURIComponent(version)}`;

export const listReleases = () =>
  request<HeadsetRelease[]>(() => getAxiosInstance().get(HEADSET_RELEASES_PATH, authorized()));

export const getRelease = (version: string) =>
  request<HeadsetRelease>(() => getAxiosInstance().get(releasePath(version), authorized()));

export const getFleetSummary = () =>
  request<FleetSummary>(() =>
    getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/fleet`, authorized()),
  );

export const getAudienceTargets = () =>
  request<AudienceTargets>(() =>
    getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/targets`, authorized()),
  );

export async function getDefaultVersion(hardwareModel: string): Promise<DefaultVersion | null> {
  try {
    return await request<DefaultVersion>(() =>
      getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/default-version`, {
        ...authorized(),
        params: { hardwareModel },
      }),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export const getOffersForDevice = (pid: string) =>
  request<ReleaseOffer[]>(() =>
    getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/offers`, {
      ...authorized(),
      params: { pid },
    }),
  );

export const getOffersForOrg = (orgId: string, hardwareModel?: string) =>
  request<ReleaseOffer[]>(() =>
    getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/offers`, {
      ...authorized(),
      params: { orgId, hardwareModel },
    }),
  );

export const createRelease = (hardware: HardwareFamily[], uploads: FamilyUpload[]) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().post(HEADSET_RELEASES_PATH, { hardware, uploads }, authorized()),
  );

export const updateRelease = (version: string, settings: ReleaseSettings) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().patch(releasePath(version), settings, authorized()),
  );

export const addReleaseApk = (version: string, { uploadId, family }: FamilyUpload) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().post(`${releasePath(version)}/apks`, { uploadId, family }, authorized()),
  );

export const addAudienceEntry = (version: string, target: AudienceTarget, mode: InstallMode) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().post(`${releasePath(version)}/audience`, { target, mode }, authorized()),
  );

export const updateAudienceEntry = (version: string, entryId: string, mode: InstallMode) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().patch(`${releasePath(version)}/audience/${entryId}`, { mode }, authorized()),
  );

export const removeAudienceEntry = (version: string, entryId: string) =>
  request<HeadsetRelease>(() =>
    getAxiosInstance().delete(`${releasePath(version)}/audience/${entryId}`, authorized()),
  );

export const getHeadsetGroups = (
  version: string,
  query: { groupBy: HeadsetGroupBy; entryId?: string; status?: HeadsetProgressFilter },
) =>
  request<HeadsetGroupsResult>(() =>
    getAxiosInstance().get(`${releasePath(version)}/headsets/groups`, {
      ...authorized(),
      params: query,
    }),
  );

export const getReleaseHeadsets = (
  version: string,
  query: HeadsetSelection & { status?: HeadsetProgressFilter; q?: string; page?: number },
) =>
  request<ReleaseHeadsetPage>(() =>
    getAxiosInstance().get(`${releasePath(version)}/headsets`, {
      ...authorized(),
      params: query,
    }),
  );

export const installOnOnlineHeadsets = (version: string, selection: HeadsetSelection) =>
  request<{ sent: number; requested: number }>(() =>
    getAxiosInstance().post(`${releasePath(version)}/install`, selection, authorized()),
  );

export const listStoredApks = () =>
  request<StoredApk[]>(() =>
    getAxiosInstance().get(`${HEADSET_RELEASES_PATH}/stored-apks`, authorized()),
  );

export const pickStoredApk = ({ version, family }: StoredApk) =>
  request<{ uploadId: string; inspection: ApkInspection }>(() =>
    getAxiosInstance().post(
      `${HEADSET_RELEASES_PATH}/apk-uploads/stored`,
      { version, family },
      { ...authorized(), timeout: APK_UPLOAD_COMPLETE_TIMEOUT_MS },
    ),
  );

export const discardApkUpload = (uploadId: string) =>
  request<void>(() =>
    getAxiosInstance().delete(`${HEADSET_RELEASES_PATH}/apk-uploads/${uploadId}`, authorized()),
  );

export async function uploadApk(
  file: File,
  onProgress: (fraction: number) => void,
  cancelled?: AbortSignal,
): Promise<{ uploadId: string; inspection: ApkInspection }> {
  const {
    uploadId,
    partSize,
    partUrls: presignedPartUrls,
  } = await request<{
    uploadId: string;
    partSize: number;
    partUrls: string[];
  }>(() =>
    getAxiosInstance().post(
      `${HEADSET_RELEASES_PATH}/apk-uploads`,
      { fileName: file.name, sizeBytes: file.size },
      authorized(),
    ),
  );

  const uploadedBytes = new Array<number>(presignedPartUrls.length).fill(0);
  const reportProgress = () =>
    onProgress(uploadedBytes.reduce((sum, bytes) => sum + bytes, 0) / file.size);
  const parts = new Array<{ partNumber: number; etag: string }>(presignedPartUrls.length);
  const partUploads = new AbortController();
  let nextPart = 0;

  const uploadNextParts = async () => {
    while (!partUploads.signal.aborted && nextPart < presignedPartUrls.length) {
      const index = nextPart++;
      const chunk = file.slice(index * partSize, (index + 1) * partSize);
      const response = await axios.put(presignedPartUrls[index], chunk, {
        signal: partUploads.signal,
        onUploadProgress: (event) => {
          uploadedBytes[index] = event.loaded;
          reportProgress();
        },
      });
      const etag = response.headers.etag;
      if (!etag) throw new Error("The storage bucket does not expose the ETag of uploaded parts");
      parts[index] = { partNumber: index + 1, etag: String(etag) };
    }
  };

  const cancelPartUploads = () => partUploads.abort();
  cancelled?.addEventListener("abort", cancelPartUploads);
  try {
    cancelled?.throwIfAborted();
    await Promise.all(Array.from({ length: PARALLEL_PART_UPLOADS }, uploadNextParts));
    cancelled?.throwIfAborted();
    const inspection = await request<ApkInspection>(() =>
      getAxiosInstance().post(
        `${HEADSET_RELEASES_PATH}/apk-uploads/${uploadId}/complete`,
        { parts },
        { ...authorized(), timeout: APK_UPLOAD_COMPLETE_TIMEOUT_MS },
      ),
    );
    cancelled?.throwIfAborted();
    return { uploadId, inspection };
  } catch (error) {
    partUploads.abort();
    await discardApkUpload(uploadId).catch(() => undefined);
    throw error;
  } finally {
    cancelled?.removeEventListener("abort", cancelPartUploads);
  }
}
